import { ReactNode, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface NotificationProps {
  show: boolean;
  message: ReactNode;
  onClose: () => void;
  title?: string;
  type?: "success" | "error" | "general";
  autoClose?: boolean;
  duration?: number;
}

export default function Notification({
  show,
  message,
  onClose,
  title,
  type = "general",
  autoClose = true,
  duration = 5000,
}: NotificationProps) {
  useEffect(() => {
    if (show && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [show, autoClose, duration, onClose]);

  const getTypeStyles = () => {
    switch (type) {
      case "success":
        return "bg-green-600 text-white";
      case "error":
        return "bg-red-600 text-white";
      case "general":
      default:
        return "bg-green-600 text-white";
    }
  };

  return (
    <div className="fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 z-[60]">
      <div className="flex w-full flex-col items-end space-y-4">
        <div
          className={`
            max-w-sm w-full ${getTypeStyles()} shadow-xl rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5
            transform transition-all ease-out duration-300
            backdrop-blur-none backdrop-filter-none
            ${
              show
                ? "translate-y-0 opacity-100 sm:translate-x-0 animate-bounce-once"
                : "translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            }
          `}
        >
          <div className="p-4">
            <div className="flex items-start">
              <div className="flex-1">
                {title && <h3 className="text-sm font-medium mb-1">{title}</h3>}
                <div className="text-sm">{message}</div>
              </div>
              <div className="ml-4 flex-shrink-0 flex">
                <button
                  type="button"
                  className="absolute top-2 right-2 rounded-full p-1 bg-white/20 hover:bg-white/30 transition-colors duration-200"
                  onClick={onClose}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon
                    className="h-4 w-4 text-white"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
